import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid, Package, Newspaper, CalendarCheck, MessageSquare, CalendarDays, FileText, Users, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import WorkshopsAdmin from "./admin/WorkshopsAdmin";
import ProductsAdmin from "./admin/ProductsAdmin";
import NewsAdmin from "./admin/NewsAdmin";
import ReservationsAdmin from "./admin/ReservationsAdmin";
import MessagesAdmin from "./admin/MessagesAdmin";
import CalendarAdmin from "./admin/CalendarAdmin";
import QuotesAdmin from "./admin/QuotesAdmin";
import ClientsAdmin from "./admin/ClientsAdmin";
import ReplacementsAdmin from "./admin/ReplacementsAdmin";

function TabTrig({ value, icon: Icon, label, testid, badge }) {
  return (
    <TabsTrigger value={value} data-testid={testid} className="relative data-[state=active]:bg-sky-500 data-[state=active]:text-white rounded-full px-4 py-2 gap-2">
      <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{label}</span>
      {badge > 0 && (
        <span data-testid="replacements-badge" className="absolute -top-1.5 -right-1.5 grid place-items-center min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold animate-pulse">
          {badge}
        </span>
      )}
    </TabsTrigger>
  );
}

function playAlert() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const beep = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur);
    };
    beep(880, 0, 0.18); beep(1180, 0.2, 0.22);
  } catch { /* audio not available */ }
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [openCount, setOpenCount] = useState(0);
  const prevCount = useRef(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  const poll = useCallback(() => {
    api.get("/admin/replacements/open-count").then((r) => {
      const n = r.data.count;
      if (prevCount.current !== null && n > prevCount.current) {
        playAlert();
        toast.warning(`Nouveau désistement ! ${n} remplacement(s) urgent(s) en attente.`, { duration: 8000 });
      }
      prevCount.current = n;
      setOpenCount(n);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, [user, poll]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Back-office</span>
        <h1 className="font-heading font-black text-4xl text-slate-900 mt-1">Tableau de bord</h1>
      </motion.div>

      <Tabs defaultValue="workshops">
        <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto mb-8">
          <TabTrig value="workshops" icon={LayoutGrid} label="Ateliers" testid="tab-workshops" />
          <TabTrig value="products" icon={Package} label="Produits" testid="tab-products" />
          <TabTrig value="news" icon={Newspaper} label="Actualités" testid="tab-news" />
          <TabTrig value="reservations" icon={CalendarCheck} label="Réservations" testid="tab-reservations" />
          <TabTrig value="quotes" icon={FileText} label="Devis" testid="tab-quotes" />
          <TabTrig value="clients" icon={Users} label="Clients" testid="tab-clients" />
          <TabTrig value="calendar" icon={CalendarDays} label="Calendrier" testid="tab-calendar" />
          <TabTrig value="replacements" icon={LifeBuoy} label="Remplacements" testid="tab-replacements" badge={openCount} />
          <TabTrig value="messages" icon={MessageSquare} label="Messages" testid="tab-messages" />
        </TabsList>

        <TabsContent value="workshops"><WorkshopsAdmin /></TabsContent>
        <TabsContent value="products"><ProductsAdmin /></TabsContent>
        <TabsContent value="news"><NewsAdmin /></TabsContent>
        <TabsContent value="reservations"><ReservationsAdmin /></TabsContent>
        <TabsContent value="quotes"><QuotesAdmin /></TabsContent>
        <TabsContent value="clients"><ClientsAdmin /></TabsContent>
        <TabsContent value="calendar"><CalendarAdmin /></TabsContent>
        <TabsContent value="replacements"><ReplacementsAdmin /></TabsContent>
        <TabsContent value="messages"><MessagesAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
